import { Box, Button, CircularProgress, Fade, Tooltip } from "@material-ui/core";
import { Help } from "@material-ui/icons";
import { useEffect, useMemo, useState } from "react";
import DropdownComponent from "../../Components/Dropdown/DropdownComponent";
import { useDebouncedHandler } from "../../Helpers/debounced";
import { useFeaturesEnabledQuery } from "../../Queries/FeaturesEnabledQueries";
import { FeaturesEnabledType } from "../../Types/FeaturesEnabled";
import { SearchInput } from "./FeaturesEnabledPage";
import { getColumnUseRate, sortUsageData, usageTableData, UsageTableRow } from "./Functions";
import SharedTable from "./SharedTable";
import { createCsvConfigAndFormatter } from "../../Helpers/csvExportHelpers";
import { download, generateCsv } from "export-to-csv";

const columns = [
  { label: "Client Name", propName: "clientName" },
  { label: "Site URL", propName: "siteUrl" },
  { label: "Brand", propName: "AdBrandID" },
  { label: "Department", propName: "AdDepartmentID" },
  { label: "Salary", propName: "AdSalaryID" },
  { label: "Schedule", propName: "AdScheduleID" },
  { label: "Shift", propName: "AdShiftID" },
  { label: "Type", propName: "AdTypeID" },
  { label: "Category", propName: "CategoryID" },
  { label: "City, State", propName: "CityState" },
  { label: "Custom Radius Collection", propName: "CustomRadiusCollection" },
  { label: "Facility", propName: "FacilityID" },
  { label: "Facility Name", propName: "FacilityName" },
  { label: "Family", propName: "FamilyID" },
  { label: "Geolocation", propName: "Geolocation" },
  { label: "Industry", propName: "IndustryID" },
  { label: "Keyword", propName: "Keyword" },
  { label: "Large Radius Collection", propName: "LargeRadiusCollection" },
  { label: "Location Name", propName: "LocationName" },
  { label: "Medium Radius Collection", propName: "MediumRadiusCollection" },
  { label: "Posted Date", propName: "PostedDate" },
  { label: "Remote Jobs", propName: "RemoteJobs" },
  { label: "Small Radius Collection", propName: "SmallRadiusCollection" },
];

export default function SearchSettingsTable() {
  const [usageType, setUsageType] = useState<FeaturesEnabledType>(FeaturesEnabledType.SearchStatic);
  const [querySearch, setQuerySearch] = useState<string>("");
  const [sortedProperty, setSortedProperty] = useState("clientName");
  const [ascending, setAscending] = useState(true);
  const [sortedData, setSortedData] = useState<UsageTableRow[]>([]);

  const usageRequest = useMemo(() => ({ type: usageType, search: querySearch }), [usageType, querySearch]);
  const { data: usage = [], isLoading, isFetching, isError } = useFeaturesEnabledQuery(usageRequest, (data) =>
    usageTableData(data)
  );
  const { data: lastUpdated = "" } = useFeaturesEnabledQuery(usageRequest, (data) =>
    data ? new Date(data.lastUpdated).toLocaleString() : ""
  );

  const handleSearchChange = useDebouncedHandler((value: string) => setQuerySearch(value), 300);

  function sortTable(column: TableColumn) {
    if (column.propName === sortedProperty) {
      setAscending(!ascending);
    } else {
      setSortedProperty(column.propName);
      setAscending(true);
    }
  }

  const columnsWithRates = useMemo(
    () =>
      columns.map((column) => {
        if (column.propName === "clientName" || column.propName === "siteUrl") {
          return column;
        }
        return {
          ...column,
          label: `${column.label} (${getColumnUseRate(sortedData, column.propName)})`,
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(sortedData)]
  );

  const csvFileName = `${usageType}_Search_Forms_Report`;

  const { csvOptions, formatRows } = createCsvConfigAndFormatter(columnsWithRates, csvFileName, csvFileName);

  const handleExport = () => {
    const csv = generateCsv(csvOptions)(formatRows(sortedData));
    download(csvOptions)(csv);
  };

  useEffect(() => {
    if (usage.length !== 0) {
      setSortedData(sortUsageData(usage, sortedProperty, ascending));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(usage), sortedProperty, ascending]);

  return (
    <>
      <Box
        display="flex"
        justifyContent="flex-start"
        alignItems="center"
        gridGap="1em"
        width="98%"
        marginBottom="1em"
      >
        <SearchInput value={querySearch} onChange={handleSearchChange} />
        <Box sx={{ minWidth: "16ch" }}>
          <DropdownComponent
            currValue={usageType}
            items={[
              { name: "Static Search", value: FeaturesEnabledType.SearchStatic },
              { name: "Float Search", value: FeaturesEnabledType.SearchFloat },
              { name: "Map Search", value: FeaturesEnabledType.SearchMap },
            ]}
            property="name"
            title="Usage Type"
            handleChange={(value: FeaturesEnabledType) => setUsageType(value)}
            multi={false}
            error={isError}
            itemIdSelector={(item: { name: string; value: FeaturesEnabledType }) => item.value}
          />
        </Box>
        <Box>
          <Tooltip
            arrow
            placement="right"
            title="Indicates which search form fields for the selected form type are being used for the given Careers Client. Column headers include the percentage of Clients using each form field."
          >
            <Help color="disabled" />
          </Tooltip>
        </Box>
        <Button variant="contained" color="primary" onClick={handleExport}>
          Export Data
        </Button>
        <Fade in={isFetching} unmountOnExit>
          <Box sx={{ position: "relative", alignSelf: "center" }}>
            <CircularProgress size="1.5rem" />
          </Box>
        </Fade>
        {lastUpdated.length > 0 ? (
          <Box marginLeft="auto" marginTop="auto" fontSize="0.85em">
            <strong>Last Updated:</strong> {lastUpdated}
          </Box>
        ) : null}
      </Box>
      <SharedTable
        data={sortedData}
        columns={columnsWithRates}
        isLoading={isLoading}
        sortedProperty={sortedProperty}
        onSort={sortTable}
        ascending={ascending}
        error={isError}
      />
    </>
  );
}
